import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import useLocalStorage, { STORAGE_KEYS } from "./useLocalStorage";
import ProjectService from "../services/ProjectService";
import PositionService from "../services/PositionService";
import UserService from "../services/UserService";
import { QueryKeyFactory } from "../services/queryKeys";
import { Routes } from "../types/Routes";
import { AssignationStatusFilters } from "../utils/enums/filters/AssignationStatusFilters";
import useUpdateUrl from "./useUpdateUrl";
import { ProjectTimelineEntry, ProjectTimelinePosition } from "../types/ProjectTimelineTypes";
import RoleUtils from "../utils/RoleUtils";
import { PositionStatusFilters } from "../utils/enums/filters/PositionStatusFilters";
import { unwrap } from "../utils/queryFn";
import { TABLE_QUERY_STALE_TIME } from "../utils/tableQuery";

const projectKeys = new QueryKeyFactory(ProjectService.path);
const positionKeys = new QueryKeyFactory(PositionService.path);
const userKeys = new QueryKeyFactory(UserService.path);
const NO_PROJECTS: AutoCompleteUIDTO[] = [];
const NO_POSITIONS: ProjectTimelinePosition[] = [];
const NO_ROWS: ProjectTimelineEntry[] = [];

function toTimelinePosition(position: {
  externalId?: string;
  externalRoleName: string;
  internalRoleName: string;
  shortStartDate: Date | string;
  shortEndDate?: Date | string;
}): ProjectTimelinePosition {
  return {
    externalId: position.externalId!,
    externalName: position.externalRoleName,
    internalName: position.internalRoleName,
    startDate: new Date(position.shortStartDate),
    endDate: position.shortEndDate ? new Date(position.shortEndDate) : undefined,
  };
}

const useProjectTimeline = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>(AssignationStatusFilters.ACTIVE);
  const [selectedProject, setSelectedProject] = useState<AutoCompleteUIDTO | undefined>(undefined);
  const [storedProject, setStoredProject] = useLocalStorage<AutoCompleteUIDTO>(STORAGE_KEYS.STORED_PROJECT, EMPTY_AUTOCOMPLETE);
  const [positionStatusFilter, setPositionStatusFilter] = useState<string>(PositionStatusFilters.ALL);
  const projectExternalId = selectedProject?.externalId;

  const searchParams = new URLSearchParams(useLocation().search);
  const project = searchParams.get("project");
  const status = searchParams.get("status");
  const { updateUrl } = useUpdateUrl(Routes.PROJECTS_TIMELINE, searchParams);

  const projectsQuery = useQuery({
    queryKey: projectKeys.list({ scope: "timeline-names", onlyExternal: false, assigned: false }),
    queryFn: () => unwrap(ProjectService.findAllProjectsNames(false, false)),
    staleTime: TABLE_QUERY_STALE_TIME,
  });
  const projectsNames = projectsQuery.data ?? NO_PROJECTS;

  const positionsQuery = useQuery({
    queryKey: positionKeys.list({ projectExternalId: projectExternalId ?? "", page: 0, pageSize: 50, roleKey: "All", active: true }),
    queryFn: () => unwrap(PositionService.findByProjectExternalIdAndRoleKey(0, 50, projectExternalId!, "All", true)),
    enabled: !!projectExternalId,
  });

  const positionNames = useMemo(() => positionsQuery.data?.content.map(toTimelinePosition) ?? NO_POSITIONS, [positionsQuery.data]);

  const rowsQuery = useQuery({
    queryKey: projectKeys.detail(`timeline:${projectExternalId ?? ""}`),
    queryFn: () => unwrap(ProjectService.getTimeline(projectExternalId!)),
    enabled: !!projectExternalId,
  });

  const managerQuery = useQuery({
    queryKey: projectKeys.detail(`manager:${projectExternalId ?? ""}`),
    queryFn: async () => {
      if (RoleUtils.isAdmin()) return true;
      try {
        await unwrap(ProjectService.isUserCoreManagerFromProject(projectExternalId!, AuthService.getUser().externalId));
        return true;
      } catch {
        return false;
      }
    },
    enabled: !!projectExternalId,
  });

  useEffect(() => {
    if (project !== null && projectsNames.length > 0) {
      const projectFilter = projectsNames.find((p) => p.externalId === project);
      if (projectFilter) setSelectedProject(projectFilter);
    } else if (storedProject !== null && projectsNames.length > 0) {
      const projectFilter = projectsNames.find((p) => p.externalId === storedProject.externalId);
      if (projectFilter) setSelectedProject(projectFilter);
      searchParams.set("project", storedProject.externalId);
    }

    if (status !== null) {
      setStatusFilter(status);
    } else {
      searchParams.set("status", AssignationStatusFilters.ALL);
    }
    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsNames]);

  const handleSelectProject = (nextProject: AutoCompleteUIDTO) => {
    setSelectedProject(nextProject ?? EMPTY_AUTOCOMPLETE);
    setStoredProject(nextProject ?? EMPTY_AUTOCOMPLETE);
    if (nextProject?.externalId) {
      searchParams.set("project", nextProject.externalId);
    } else {
      searchParams.delete("project");
    }
    updateUrl();
  };

  const onAssignationUpdate = (updated: boolean) => {
    if (projectExternalId && updated) {
      void queryClient.invalidateQueries({ queryKey: projectKeys.detail(`timeline:${projectExternalId}`) });
    }
  };

  const fetchPositions = () => {
    if (!projectExternalId) return;
    void queryClient.invalidateQueries({
      queryKey: positionKeys.list({ projectExternalId, page: 0, pageSize: 50, roleKey: "All", active: true }),
    });
  };

  const loadUserNames = async (): Promise<AutoCompleteUIDTO[] | undefined> => {
    try {
      return await queryClient.fetchQuery({
        queryKey: userKeys.list({ scope: "names" }),
        queryFn: () => unwrap(UserService.findUserNames()),
        staleTime: TABLE_QUERY_STALE_TIME,
      });
    } catch {
      NotificationService.error("There was an error getting the user names");
      return undefined;
    }
  };

  const isLoading = projectsQuery.isFetching || positionsQuery.isFetching || rowsQuery.isFetching || managerQuery.isFetching;

  return {
    userIsManager: !!managerQuery.data,
    projectsNames,
    positionNames,
    handleSelectProject,
    statusFilter,
    setStatusFilter,
    isLoading,
    selectedProject,
    setSelectedProject,
    rows: rowsQuery.data ?? NO_ROWS,
    onAssignationUpdate,
    fetchPositions,
    loadUserNames,
    positionStatusFilter,
    setPositionStatusFilter,
  };
};

export default useProjectTimeline;
